// @flow
/** @jsx isolateComponent */

import Head from 'next/head';
import { flatMap } from 'lodash';
import { withRouter } from 'next/router';
import { Fragment, type Node } from 'react';

import { Screen } from '@zapier/theme';
import { Separator } from '@zapier/design-system-app-profiles';
import {
  Badge,
  Colors,
  Heading,
  Spacer,
  Typography,
} from '@zapier/design-system';
import {
  GridLayout,
  HeadingLevel,
  useMediaProps,
} from '@zapier/common-components';
import { TrackTime, useTrackingContext } from '@zapier/tracking';
import {
  ensureArray,
  isolateComponent,
  withModifier,
} from '@zapier/common-utils';

import AppCallout from 'web-ssr/app/app-directory/components/AppCallout';
import AppDirectoryNav from 'web-ssr/app/app-directory/components/AppDirectoryNav';
import AppOverview from 'web-ssr/app/app-directory/components/AppOverview';
import AppsOverview from 'web-ssr/app/app-directory/components/AppsOverview';
import ContentFooter from 'web-ssr/app/common/components/ContentFooter';
import ContentHeader from 'web-ssr/app/common/components/ContentHeader';
import LDJSONStructuredData from 'web-ssr/app/common/components/LDJSONStructuredData';
import SignupForm from 'web-ssr/app/app-directory/components/SignupForm';
import StickyNode from 'web-ssr/app/common/components/StickyNode';
import useServiceTracking from 'web-ssr/app/common/tracking/hooks/useServiceTracking';
import SharingMetaTags, {
  type SharingMetaTagsType,
  buildSharingUrl,
} from 'web-ssr/app/common/components/SharingMetaTags';
import { buildStaticAssetPath } from 'web-ssr/app/common/next/utils';
import { createAppOverviewPageLink } from 'web-ssr/app/app-directory/utils';

import type { LDJSONStructuredDataType } from 'web-ssr/app/common/components/LDJSONStructuredData';
import type { RouterProps } from 'web-ssr/app/common/next/types';

type AppProps = {
  id: string,
  name: string,
  slug: string,
  currentImplementation: {
    id: string,
  },
};

type Props = {
  /** The title of the address bar */
  documentTitle: string,
  /** Used by social media sites when the page is shared */
  documentDescription: string,
  /** Any additional breadcrumbs to be added to breadcrumb trail */
  extraCrumbs: Array<{ href: string, text: string }>,
  /** The title of the page, rendered in an H1 */
  pageTitle?: string,
  /** The sub-title of the page */
  pageSubTitle?: string,
  /** Optional canonical URL of the page */
  canonicalUrl?: string,
  /** Content to render on the page */
  children: Node,
  /**
   * Whether to render a `noindex` meta tag or not. Discourages SEO crawlers
   * from indexing the content.
   **/
  shouldIndex: boolean,
  /** Toggles the app directory sub-naviation */
  showNavigation: boolean,
  /** The app which is the primary focus of the page */
  primaryApp: AppProps,
  /** The app which is the secondary focus of the page */
  secondaryApp?: AppProps,
  /** Next.js router instance */
  router: RouterProps,
  /** Parent determines what to show for the overview */
  renderOverview: () =>
    | React.Element<typeof AppsOverview>
    | React.Element<typeof AppOverview>
    | null,
  /** LD+JSON Structured Data */
  structuredData: LDJSONStructuredDataType,
};

const Styles = {
  root: size => [
    withModifier(
      size,
      {
        small: { padding: '0' },
        medium: { padding: '0 20px' },
        large: { padding: '0 40px' },
      }[size]
    ),
  ],
  chrome: size => [
    {
      borderBottom: `1px solid ${Colors.grey100}`,
    },
    withModifier(
      size,
      {
        small: { padding: '0' },
        medium: { padding: '0 20px' },
        large: { padding: '10px 40px' },
      }[size]
    ),
  ],
  container: {
    maxWidth: 1200,
    margin: '0 auto',
  },
  sidebar: {
    gridArea: 'sidebar',
    overflow: 'visible', // necessary for tooltips to overlay the main column
    maxWidth: '100%',
  },
  content: {
    gridArea: 'content',
    width: '100%',
  },
  // the gap between modules in content is not vertically symmetrical
  // so we set extra margin to increase the top spacing
  separator: size => [
    {
      marginTop: 10,
      // Don't render Separator as first child, last child, or immediate sibling of Separator.
      // This handles cases where a React element in the start, middle, or end of the
      // page layout could return `null`.
      '& + &': {
        display: 'none',
        visibility: 'hidden',
      },
      '&:last-child': {
        display: 'none',
        visibility: 'hidden',
      },
      '&:first-child': {
        display: 'none',
        visibility: 'hidden',
      },
      // ensure that separators show after adjacent <section>s, not just <div>s
      'section + &:first-of-type': {
        display: 'block',
        visibility: 'visible',
      },
    },
    size === 'small' &&
      withModifier(size, {
        marginTop: 20,
      }),
  ],
  pageContent: size => [
    size === 'small' && withModifier(size, { padding: '0 10px' }),
  ],
  breadcrumbs: {
    margin: '20px 0',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    visibility: 'visible',
  },
  breadcrumbItem: {
    '&:not(:last-child)': {
      margin: '0 10px 0 0',
    },
  },
  pageTitle: {
    ...Typography.header800,
    fontWeight: 700,
  },
  pageSubTitle: size => [
    {
      ...Typography.subtitle900,
      padding: '0 10px',
    },
    size === 'small' &&
      withModifier(size, {
        ...Typography.subtitle800,
        padding: '0 20px',
      }),
  ],
};

const AppDirectoryLayout = (props: Props) => {
  const {
    canonicalUrl,
    children,
    documentTitle,
    pageSubTitle,
    pageTitle,
    primaryApp,
    renderOverview,
    secondaryApp,
    showNavigation,
    structuredData,
  } = props;
  const { trackEvent } = useTrackingContext();

  /** TODO: This CANNOT be removed until after AB testing of the app-directory pages is completed. */
  const shouldIndex = false;

  const { size, signupFooter } = useMediaProps({
    [Screen.queries.phone]: { size: 'small', signupFooter: 'small' },
    [Screen.queries.phablet]: { size: 'small', signupFooter: 'medium' },
    [Screen.queries.tablet]: { size: 'medium', signupFooter: 'medium' },
    [Screen.queries.desktop]: { size: 'large', signupFooter: 'medium' },
  });

  useServiceTracking({
    primaryService: primaryApp,
    secondaryService: secondaryApp,
  });

  const mobileOrTabletLayout = {
    areas: `
      "sidebar"
      "content"
    `,
    gap: 0,
    columns: '100%',
  };

  const gridLayoutQueries = {
    [Screen.queries.phone]: mobileOrTabletLayout,
    [Screen.queries.phablet]: mobileOrTabletLayout,
    [Screen.queries.tablet]: mobileOrTabletLayout,
    [Screen.queries.desktop]: {
      areas: `
        "sidebar content content content"
      `,
      gap: 40,
      columns: '265px repeat(3, 1fr)',
    },
  };

  const sharingMetaTags: SharingMetaTagsType = {
    title: documentTitle,
    description: props.documentDescription,
    url: buildSharingUrl(props.router.asPath),
    image: buildStaticAssetPath('images/social.png'),
  };

  const breadCrumbs = [
    {
      text: 'Apps',
      href: '/apps',
    },
    {
      text: primaryApp.name,
      href: createAppOverviewPageLink(primaryApp.slug),
    },
    ...props.extraCrumbs,
  ];

  /**
   * Build an array of children plus the modules always at the bottom of the page,
   * then insert Separators between each of the modules, which get spaced out appropriately
   * by the inner GridLayout's `gap` in the main column.
   *
   * This allows each page to just hand in their modules as children, and let this layout component
   * worry about the module spacing. To keep two components grouped in the same module, just wrap
   * them in a containing element like a `div`.
   */
  const contentModules = ensureArray(children).concat(
    <SignupForm size={signupFooter} />,
    <AppCallout appSlug={primaryApp.slug} />
  );
  const spacedContentModules = flatMap<Node, Node>(
    contentModules,
    (val: Node, i: number, modules: Node[]) =>
      modules.length - 1 !== i // check for the last item
        ? [
            val,
            <div css={Styles.separator(size)}>
              <Separator
                height={size === 'small' ? '2px' : '3px'}
                isFullWidth={true}
              />
            </div>,
          ]
        : val
  );

  return (
    <Fragment>
      <div css={Styles.chrome(size)}>
        <ContentHeader isDesignSystemHeader={true} />
      </div>
      <TrackTime
        track={(eventName, metaData) => {
          trackEvent(eventName, metaData);
        }}
      >
        <div css={Styles.root(size)}>
          <Head>
            <title>{documentTitle}</title>
            {canonicalUrl && <link href={props.canonicalUrl} rel="canonical" />}
            {structuredData && <LDJSONStructuredData {...structuredData} />}
            <SharingMetaTags {...sharingMetaTags} />
            <meta content={shouldIndex ? 'all' : 'noindex'} name="robots" />
          </Head>

          <div css={Styles.container}>
            {size !== 'small' && size !== 'medium' && (
              <nav aria-label="breadcrumb">
                <ol css={Styles.breadcrumbs}>
                  {breadCrumbs.map((crumb, index) => (
                    <li
                      css={Styles.breadcrumbItem}
                      key={`app-directory-layout-breadcrumb--${index}--url-${encodeURIComponent(
                        crumb.href
                      )}`}
                    >
                      <Badge
                        ariaCurrent={
                          index === breadCrumbs.length - 1 ? 'page' : false
                        }
                        color="light"
                        href={crumb.href}
                      >
                        {crumb.text}
                      </Badge>
                    </li>
                  ))}
                </ol>
              </nav>
            )}

            <GridLayout
              areas={`
                "sidebar"
                "content"
              `}
              propsQueries={gridLayoutQueries}
            >
              <div
                aria-label="app description and sign up form"
                css={Styles.sidebar}
                role="complementary"
              >
                {/* This renders either `<AppOverview>` or `<AppsOverview>` depending on the page */}
                {renderOverview()}

                {/* The side bar signup form only exists on desktop */}
                {size === 'large' && (
                  <Fragment>
                    <Spacer height="40px" />
                    <Separator isFullWidth={true} />
                    <Spacer height="40px" />

                    <StickyNode top={40}>
                      <SignupForm size="large" />
                    </StickyNode>
                  </Fragment>
                )}
              </div>

              <main css={Styles.content}>
                {pageTitle && size === 'large' && (
                  <HeadingLevel>
                    <Heading css={Styles.pageTitle}>{pageTitle}</Heading>
                    <Spacer height="20px" />
                  </HeadingLevel>
                )}

                {showNavigation && (
                  <Fragment>
                    <AppDirectoryNav primaryAppSlug={primaryApp.slug} />
                    <Spacer height={size === 'small' ? 30 : 40} />
                  </Fragment>
                )}

                {pageSubTitle && (
                  <Fragment>
                    <Heading css={Styles.pageSubTitle(size)}>
                      {pageSubTitle}
                    </Heading>
                    <Spacer height={size === 'small' ? 30 : 40} />
                  </Fragment>
                )}

                <div css={Styles.pageContent(size)}>
                  <GridLayout columns="100%" gap={size === 'small' ? 30 : 40}>
                    {spacedContentModules}
                  </GridLayout>
                </div>
              </main>
            </GridLayout>
          </div>
        </div>
      </TrackTime>
      <div css={Styles.chrome(size)}>
        <ContentFooter isDesignSystemFooter={true} />
      </div>
    </Fragment>
  );
};

AppDirectoryLayout.defaultProps = {
  documentTitle: 'Zapier | The easiest way to automate your work',
  showNavigation: true,
  shouldIndex: true,
  extraCrumbs: [],
  renderOverview: () => null,
};

export default withRouter(AppDirectoryLayout);
